import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfidencePill } from "@/components/n360/Badges";

const inputCls = "w-full px-3 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#0D3C4C] placeholder:text-[#aab9bd] focus:outline-none focus:border-[#F1551D]/60 text-sm";

const AREAS = ["Public-Profile Research", "Professional & Career", "Organizational Relationships", "Industry & Market", "Network / Relationship", "Public Reputation & Media", "Business & Ownership", "Events & Institutional Intelligence"];

export default function IntelligenceDesk() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.role === "field_associate" || user?.role === "admin" ? "queue" : "submit");
  const [queue, setQueue] = useState([]);
  const [mine, setMine] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [form, setForm] = useState({ subject_profile_id: "", area: AREAS[0], claim: "", source: "", confidence: 70 });
  const isFA = ["field_associate", "admin"].includes(user?.role);

  const loadQueue = () => api.get("/contributions", { params: { scope: "queue" } }).then(({ data }) => setQueue(data.contributions));
  const loadMine = () => api.get("/contributions", { params: { scope: "mine" } }).then(({ data }) => setMine(data.contributions));

  useEffect(() => {
    if (isFA) loadQueue();
    loadMine();
    api.get("/directory").then(({ data }) => setDirectory(data.results));
  }, [isFA]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject_profile_id || !form.claim) { toast.error("Select a subject and enter a claim."); return; }
    try {
      await api.post("/contributions", { ...form, confidence: Number(form.confidence) });
      toast.success("Intelligence submitted for verification.");
      setForm({ ...form, claim: "", source: "" });
      loadMine();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const verify = async (cid, decision) => {
    const note = decision === "verified" ? "Corroborated against sources" : decision === "rejected" ? "Insufficient / unreliable" : "Needs additional corroboration";
    try {
      await api.post(`/contributions/${cid}/verify`, { decision, note });
      toast.success(`Recorded: ${decision}`);
      loadQueue();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const statusTone = (s) => s === "verified" ? "border-emerald-500/30 text-emerald-300" : s === "rejected" ? "border-rose-500/30 text-rose-300" : "border-amber-500/30 text-[#B84214]";

  const Card = ({ c, actions }) => (
    <div className="rounded-lg border border-[#F1551D]/12 bg-[#FEF7E7]/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] text-[#F1551D] font-mono-i">{c.area}</div>
          <div className="text-[#154859] text-sm mt-0.5">On <span className="text-[#B84214]">{c.subject_name}</span> — by {c.contributor_name}</div>
          <p className="text-sm text-[#5f7d86] mt-1">{c.claim}</p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-[#8598a0]"><span className="flex items-center gap-1"><Icons.FileSearch className="w-3 h-3" />{c.source || "Unsourced"}</span><ConfidencePill value={c.confidence} /></div>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize shrink-0 ${statusTone(c.status)}`}>{c.status.replace("_", " ")}</span>
      </div>
      {actions}
    </div>
  );

  return (
    <div className="rise">
      <div className="mb-6">
        <div className="font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-2">Intelligence Contributor & Field Associate</div>
        <h1 className="font-display text-3xl text-[#0D3C4C]">Intelligence Desk</h1>
        <p className="text-[#5f7d86] mt-1 text-sm">Intelligence Contributors submit verified claims; Field Associates corroborate — never their own submissions.</p>
      </div>

      <div className="flex gap-2 mb-6">
        {isFA && <button onClick={() => setTab("queue")} data-testid="idesk-tab-queue" className={`px-4 py-2 rounded-full text-sm ${tab === "queue" ? "bg-[#F1551D]/15 text-[#B84214] border border-[#F1551D]/40" : "text-[#5f7d86] border border-transparent"}`}>Verification Queue ({queue.length})</button>}
        <button onClick={() => setTab("submit")} data-testid="idesk-tab-submit" className={`px-4 py-2 rounded-full text-sm ${tab === "submit" ? "bg-[#F1551D]/15 text-[#B84214] border border-[#F1551D]/40" : "text-[#5f7d86] border border-transparent"}`}>Submit Intelligence</button>
        <button onClick={() => setTab("mine")} data-testid="idesk-tab-mine" className={`px-4 py-2 rounded-full text-sm ${tab === "mine" ? "bg-[#F1551D]/15 text-[#B84214] border border-[#F1551D]/40" : "text-[#5f7d86] border border-transparent"}`}>My Contributions ({mine.length})</button>
      </div>

      {tab === "queue" && isFA && (
        <div className="space-y-3">
          {queue.length === 0 ? <div className="n360-card p-10 text-center text-[#8598a0]">Queue clear. No pending intelligence to verify.</div> :
            queue.map((c) => <Card key={c.id} c={c} actions={
              <div className="flex gap-2 mt-3 pt-3 border-t border-[#F1551D]/10">
                <button onClick={() => verify(c.id, "verified")} data-testid={`verify-approve-${c.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/25"><Icons.Check className="w-3.5 h-3.5" /> Verify</button>
                <button onClick={() => verify(c.id, "needs_more")} data-testid={`verify-more-${c.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-[#B84214] text-xs hover:bg-amber-500/25"><Icons.HelpCircle className="w-3.5 h-3.5" /> Needs More</button>
                <button onClick={() => verify(c.id, "rejected")} data-testid={`verify-reject-${c.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-500/15 border border-rose-500/40 text-rose-300 text-xs hover:bg-rose-500/25"><Icons.X className="w-3.5 h-3.5" /> Reject</button>
              </div>
            } />)}
        </div>
      )}

      {tab === "submit" && (
        <form onSubmit={submit} className="n360-card p-6 space-y-4 max-w-2xl">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Subject Principal *</div>
            <select data-testid="contrib-subject" value={form.subject_profile_id} onChange={(e) => setForm({ ...form, subject_profile_id: e.target.value })} className={inputCls}>
              <option value="" className="bg-[#F6EEDC]">Select principal…</option>
              {directory.map((d) => <option key={d.id} value={d.id} className="bg-[#F6EEDC]">{d.name} — {d.organization}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Intelligence Area</div>
            <select data-testid="contrib-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className={inputCls}>
              {AREAS.map((a) => <option key={a} value={a} className="bg-[#F6EEDC]">{a}</option>)}
            </select>
          </div>
          <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Claim / Information *</div><textarea data-testid="contrib-claim" value={form.claim} onChange={(e) => setForm({ ...form, claim: e.target.value })} rows={3} className={inputCls} placeholder="State the verifiable claim…" /></div>
          <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Source</div><input data-testid="contrib-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inputCls} placeholder="Authoritative / public source" /></div>
          <div className="flex items-center gap-2"><span className="text-xs text-[#8598a0]">Confidence</span><input type="range" min={0} max={100} value={form.confidence} onChange={(e) => setForm({ ...form, confidence: Number(e.target.value) })} className="accent-[#F1551D] flex-1" data-testid="contrib-confidence" /><span className="font-mono-i text-xs text-[#2f5561]">{form.confidence}%</span></div>
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-2.5 text-[11px] text-[#B84214]/80">Ethical boundary: submit only accurate, lawfully-obtained, verifiable information. No rumours, hacked data, or fabrications.</div>
          <button data-testid="contrib-submit-btn" className="px-6 py-2.5 rounded-full bg-[#F1551D] text-[#FEF7E7] font-medium hover:bg-[#F0A315]">Submit for Verification</button>
        </form>
      )}

      {tab === "mine" && (
        <div className="space-y-3">
          {mine.length === 0 ? <div className="n360-card p-10 text-center text-[#8598a0]">You have not submitted intelligence yet.</div> : mine.map((c) => <Card key={c.id} c={c} actions={c.verifications?.length ? <div className="mt-3 pt-3 border-t border-[#F1551D]/10 text-[11px] text-[#8598a0]">{c.verifications.length} verification record(s) · latest: {c.verifications.at(-1).decision}</div> : null} />)}
        </div>
      )}
    </div>
  );
}
